import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ComponentProps } from 'react';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Percentage } from '#src/atoms/icons/Percentage.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BrandXXS } from '#src/atoms/typography/index.tsx';
import type { Color, RGBColor } from '#src/style/types.ts';

import { BonusBreakdownTooltip } from './BonusBreakdownTooltip/index.tsx';
import type {
  BonusChip_powerBreakdown,
  BonusChip_so5AppearanceBonusInterface,
} from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ center: true })`
  min-width: var(--quadruple-unit);
  min-height: var(--chip-small-height);
  display: flex;
  gap: 1px;
  border-radius: 9999px;
  border: 1px solid var(--c-nd-400);
  background: linear-gradient(-30deg, var(--c-nd-200) 50%, var(--c-nd-100) 50%);
  padding: 0 2px;

  &.colored {
    --color: rgba(var(--rgb-color), 0.1);
    border-color: rgb(var(--rgb-color));
    background:
      linear-gradient(var(--color), var(--color)),
      linear-gradient(-30deg, var(--c-nd-200) 50%, var(--c-nd-100) 50%);
  }
`;

type Props = {
  bonuses?: BonusChip_so5AppearanceBonusInterface[];
  bonus: number;
  powerBreakdown?: BonusChip_powerBreakdown | null;
  captain?: boolean;
  captainBonus?: number | null;
  size?: 'small' | 'large';
  rgbColor?: RGBColor;
  withTransferMalus?: boolean;
  disableTooltip?: boolean;
} & Pick<ComponentProps<typeof BonusBreakdownTooltip>, 'transferMalus'>;

export const BonusChip = ({
  bonuses,
  bonus,
  powerBreakdown,
  captain,
  captainBonus,
  withTransferMalus,
  transferMalus,
  size = 'small',
  rgbColor,
  disableTooltip,
}: Props) => {
  const hasCaptainBonusAlready =
    (powerBreakdown?.captainBasisPoints || 0) !== 0;

  let actualBonus = bonus - 1;
  if (captain && !hasCaptainBonusAlready) {
    actualBonus += captainBonus || 0;
  }
  if (!captain && hasCaptainBonusAlready) {
    actualBonus -= captainBonus || 0;
  }
  if (withTransferMalus) {
    if (transferMalus) actualBonus += transferMalus;
    if (powerBreakdown?.collectionBasisPoints) {
      actualBonus -= powerBreakdown.collectionBasisPoints / 10000;
    }
  }

  const color: Color | undefined = rgbColor
    ? (`rgb(${rgbColor})` as Color)
    : undefined;
  const chip = (
    <Root
      style={{ '--rgb-color': rgbColor } as CSSProperties}
      className={classNames(size, { colored: !!color })}
    >
      <BrandXXS bold color={color}>
        <FormattedNumber value={actualBonus * 100} signDisplay="always" />
      </BrandXXS>
      <Percentage color={color} />
    </Root>
  );

  if (bonuses) {
    return (
      <BonusBreakdownTooltip skip={disableTooltip} bonuses={bonuses}>
        {chip}
      </BonusBreakdownTooltip>
    );
  }
  if (powerBreakdown) {
    const displayPowerBreakdown = {
      ...powerBreakdown,
      ...(withTransferMalus
        ? { collectionBasisPoints: 0 }
        : { collectionBasisPoints: powerBreakdown.collectionBasisPoints }),
    };

    return (
      <BonusBreakdownTooltip
        skip={disableTooltip}
        powerBreakdown={displayPowerBreakdown}
        captainBonus={captain ? captainBonus : 0}
        transferMalus={transferMalus}
      >
        {chip}
      </BonusBreakdownTooltip>
    );
  }

  return chip;
};

BonusChip.fragments = {
  powerBreakdown: gql`
    fragment BonusChip_powerBreakdown on PowerBreakdown {
      captainBasisPoints
      collectionBasisPoints
      ...BonusBreakdownTooltip_powerBreakdown
    }
    ${BonusBreakdownTooltip.fragments.powerBreakdown}
  ` as TypedDocumentNode<BonusChip_powerBreakdown>,
  so5AppearanceBonusInterface: gql`
    fragment BonusChip_so5AppearanceBonusInterface on So5AppearanceBonusInterface {
      ...BonusBreakdownTooltip_so5AppearanceBonusInterface
    }
    ${BonusBreakdownTooltip.fragments.so5AppearanceBonusInterface}
  ` as TypedDocumentNode<BonusChip_so5AppearanceBonusInterface>,
};
